// A link to something that may live in another place (a Workspace in cloud).
//
// Promise: a link to Me navigates inside this window. A link to another place
// goes to the host's `openTarget` (cloud opens that Workspace's own window or
// tab); when the host has none, or declines, it is a plain link. `onClick`
// hears a plain press either way (a dialog around it closes).
import { Link, useRouter } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useMeApp } from "../app/context";

/** Goes where a `PlaceLink` with this `href` and `place` goes on a plain press. */
export function usePlaceOpen(): (href: string, place: string) => void {
  const { extensions } = useMeApp();
  const router = useRouter();
  return (href, place) => {
    if (place === "me") void router.navigate({ to: href });
    else if (!extensions.openTarget?.(href, place))
      window.location.assign(href);
  };
}

export function PlaceLink({
  href,
  place,
  className,
  onClick,
  children,
}: {
  href: string;
  place: string;
  className?: string;
  onClick?(): void;
  children: ReactNode;
}) {
  const { extensions } = useMeApp();
  if (place === "me")
    return (
      <Link to={href} className={className} onClick={onClick}>
        {children}
      </Link>
    );
  return (
    <a
      href={href}
      className={className}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        onClick?.();
        if (extensions.openTarget?.(href, place)) e.preventDefault();
      }}
    >
      {children}
    </a>
  );
}
